import { Box, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { DeleteConfirmationBody } from '@planning/components/DeleteConfirmationBody'
import { ContainerJourneyStatus } from '@planning/components/molecules/ContainerJourneyStatusChip'
import { useOrderNavigation } from '@planning/hooks/useOrderNavigation'
import { OrderNotesSubForm } from '@planning/pages/Order/V2/organisms/OrderNotesSubForm'
import { BulkEditOrderNotesFormStore } from '@planning/pages/VesselVisit/Stores/BulkEditOrderNotesFormStore'
import { OrderListTableColumnsDto } from '@planning/pages/VesselVisit/Stores/VesselVisitOrderListViewStore'
import { adminActionService, containerService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { TomTableActionsProps } from '@tom-ui/ui'
import { MRT_Row } from 'material-react-table'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

export interface BulkEditSelectionDto {
  orderId: number
  containerJourneyId?: number
  containerNumber?: string
}

const partitionVisitsByStatus = <TData extends OrderListTableColumnsDto>(
  rows: MRT_Row<TData>[],
) => {
  const editableVisits: MRT_Row<TData>[] = []
  const departedVisits: MRT_Row<TData>[] = []

  for (const row of rows) {
    if (row.original.status === ContainerJourneyStatus.Departed) {
      departedVisits.push(row)
    } else {
      editableVisits.push(row)
    }
  }

  return { editableVisits, departedVisits }
}

const getBulkEditData = <TData extends OrderListTableColumnsDto>(
  editableVisits: MRT_Row<TData>[],
) => {
  const bulkEditSelection: BulkEditSelectionDto[] = []

  editableVisits.forEach(row => {
    bulkEditSelection.push({
      orderId: row.original.id,
      containerJourneyId: row.original.containerJourneyId ?? undefined,
      containerNumber: row.original.number ?? undefined,
    })
  })

  return bulkEditSelection
}

export const useVesselVisitTableActions = <TData extends OrderListTableColumnsDto>() => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const { openEditOrder } = useOrderNavigation()

  const { appViewStore, orderItemStore } = usePlanningStore()

  const orderNotesFormStore = useMemo(() => new BulkEditOrderNotesFormStore(), [])

  const navigateToBulkEdit = useCallback(
    (editableVisits: MRT_Row<TData>[], category: string) => {
      const bulkEditData = getBulkEditData(editableVisits)
      navigate('bulk-edit', {
        state: {
          data: bulkEditData,
          category,
        },
      })
    },
    [navigate],
  )

  const handleMixedSelection = useCallback(
    async (
      editableVisits: MRT_Row<TData>[],
      departedVisits: MRT_Row<TData>[],
      category: string,
    ) => {
      const title = t('proceedWithIncompleteOnly', 'Proceed with incomplete visits only?')
      const MessageComponent = (
        <Box>
          <Typography variant='body1' gutterBottom>
            {t(
              'editingNotAllowedForDeparted',
              'Editing is no longer allowed for the following departed container visits:',
            )}
          </Typography>
          <Box sx={{ pl: 3, pt: 1 }}>
            <ul>
              {departedVisits.map(row => (
                <li key={row.original.id}>{row.original.number}</li>
              ))}
            </ul>
          </Box>
          <Typography variant='body1' sx={{ mt: 2 }}>
            {t('messagesWillNotBeSentAgain', 'Messages that were sent will not be sent again.')}
          </Typography>
          <Typography variant='body1' sx={{ mt: 1 }}>
            {t('proceedWithIncompleteVisitsOnly', 'Proceed with incomplete visits only?')}
          </Typography>
        </Box>
      )

      const isConfirmed = await appViewStore.setOpenConfirmDialog(
        true,
        MessageComponent,
        title,
        undefined,
        {
          primaryActionText: t('yesContinue', 'Yes, continue'),
          secondaryActionText: t('noCancel', 'No, cancel'),
        },
      )

      if (isConfirmed) {
        navigateToBulkEdit(editableVisits, category)
      }
    },
    [appViewStore, navigateToBulkEdit, t],
  )

  const handleBulkEdit = useCallback(
    async (selectedRows: MRT_Row<TData>[]) => {
      const categories = new Set(selectedRows.map(row => row.original.category))
      if (categories.size !== 1) {
        const categoriesText = Array.from(categories)
          .map(cat => `<${cat}>`)
          .join(' and ')
        appViewStore.setShowAlert(
          'error',
          t(
            'mixedCategoriesError',
            'You’ve selected {categories} categories. Please choose one to edit.',
            { categories: categoriesText },
          ),
        )
        return
      }
      const [singleCategory] = categories

      const { editableVisits, departedVisits } = partitionVisitsByStatus(selectedRows)

      if (editableVisits.length > 0 && departedVisits.length === 0) {
        navigateToBulkEdit(editableVisits, singleCategory!)
      } else if (editableVisits.length === 0 && departedVisits.length > 0) {
        appViewStore.setShowAlert(
          'error',
          t(
            'visitsAlreadyDeparted',
            'Container visits have already been departed. Changes cannot be performed.',
          ),
        )
      } else if (editableVisits.length > 0 && departedVisits.length > 0) {
        await handleMixedSelection(editableVisits, departedVisits, singleCategory!)
      }
    },
    [appViewStore, handleMixedSelection, navigateToBulkEdit, t],
  )

  const handleSingleEdit = useCallback(
    (selectedRows: MRT_Row<TData>[]) => {
      const order = orderItemStore.elements[Number(selectedRows[0].original.id)].data
      if (selectedRows.length === 1) {
        openEditOrder(order)
      }
    },
    [openEditOrder, orderItemStore.elements],
  )

  const handleAddNotes = useCallback(
    async (selectedRows: MRT_Row<TData>[]) => {
      const selectedContainerJourneyIds = selectedRows
        .filter(row => row.original.containerJourneyId !== undefined)
        .map(row => row.original.containerJourneyId!)

      const isConfirm = await appViewStore.setOpenConfirmDialog(
        true,
        <OrderNotesSubForm formStore={orderNotesFormStore} />,
        t('addNotes', 'Add notes'),
        undefined,
        {
          primaryActionText: t('save', 'Save'),
          secondaryActionText: t('cancel', 'Cancel'),
          maxWidth: 'sm',
        },
      )

      if (isConfirm) {
        const cmd = {
          ids: selectedContainerJourneyIds,
          notes: orderNotesFormStore.data.notes ?? undefined,
          operationalInstructions: orderNotesFormStore.data.operationalInstructions ?? undefined,
        }
        try {
          appViewStore.showLoader()
          await containerService.bulkUpdateContainerVisitNotes(cmd)
          appViewStore.setShowAlert(
            'success',
            t('notesAddedSuccessfully', 'Notes added successfully'),
          )
        } catch (error) {
          appViewStore.setShowAlert('error', t('failedToAddNotes', 'Failed to add notes'))
          return
        } finally {
          appViewStore.hideLoader()
        }
      }
    },
    [appViewStore, orderNotesFormStore, t],
  )

  const handleBulkDelete = useCallback(
    async (selectedRows: MRT_Row<TData>[]) => {
      const deletableRows: MRT_Row<TData>[] = []
      const nonDeletableStatusCounts: { [key: string]: number } = {}

      for (const row of selectedRows) {
        if (row.original.status === ContainerJourneyStatus.Expected) {
          deletableRows.push(row)
        } else {
          const status = row.original.status ?? t('unknownStatus', 'Unknown Status')
          nonDeletableStatusCounts[status] = (nonDeletableStatusCounts[status] || 0) + 1
        }
      }

      if (deletableRows.length === 0) {
        appViewStore.setShowAlert(
          'error',
          t('noneCanBeDeletedError', "Only containers with 'Expected' status can be deleted."),
        )
        return
      }

      const deletableCount = deletableRows.length
      const isMixedSelection = Object.keys(nonDeletableStatusCounts).length > 0

      const title = isMixedSelection
        ? t(
            'deleteXOfYContainersTitle',
            'Delete {deletableCount} of {totalCount} selected containers?',
            {
              deletableCount,
              totalCount: selectedRows.length,
            },
          )
        : t('deleteXContainersTitle', 'Delete {count} containers?', { count: deletableCount })

      const MessageComponent = (
        <DeleteConfirmationBody
          deletableCount={deletableCount}
          nonDeletableStatusCounts={nonDeletableStatusCounts}
          t={t}
        />
      )

      const isConfirmed = await appViewStore.setOpenConfirmDialog(
        true,
        MessageComponent,
        title,
        undefined,
        {
          primaryActionText: t('yesDelete', 'Yes, delete'),
          secondaryActionText: t('noKeepContainers', 'No, keep containers'),
        },
      )

      if (!isConfirmed) return

      try {
        appViewStore.showLoader()
        const idsToDelete = deletableRows.map(row => row.original.id)
        await adminActionService.deleteByIds(idsToDelete)
        appViewStore.setShowAlert(
          'success',
          t(
            'successfullyDeletedOrders',
            `{count} ${idsToDelete.length === 1 ? 'container' : 'containers'} deleted successfully.`,
            {
              count: idsToDelete.length,
            },
          ),
        )
      } catch (e) {
        appViewStore.setShowAlert('error', t('failedToDeleteOrders', 'Failed to delete containers'))
      } finally {
        appViewStore.hideLoader()
      }
    },
    [appViewStore, t],
  )

  return useCallback(
    (selectedRows: MRT_Row<TData>[]): TomTableActionsProps<TData>[] => {
      const actions: TomTableActionsProps<TData>[] = []
      const selectionCount = selectedRows.length

      if (selectionCount === 1) {
        actions.push({
          label: t('edit', 'Edit'),
          grouped: true,
          onClick: () => handleSingleEdit(selectedRows),
          testId: 'single-edit-action',
        })
      } else if (selectionCount > 1) {
        actions.push({
          label: t('bulkEdit', 'Bulk edit'),
          grouped: true,
          onClick: () => handleBulkEdit(selectedRows),
          testId: 'bulk-edit-action',
        })
      }

      if (selectionCount > 0) {
        actions.push({
          label: t('addNotes', 'Add notes'),
          grouped: true,
          onClick: () => handleAddNotes(selectedRows),
          testId: 'add-notes-action',
        })

        actions.push({
          label: t('delete', 'Delete'),
          grouped: true,
          onClick: () => handleBulkDelete(selectedRows),
          testId: 'bulk-delete-action',
          disabled: selectedRows.every(r => r.original.status !== ContainerJourneyStatus.Expected),
        })
      }

      return actions
    },
    [t, handleSingleEdit, handleBulkEdit, handleAddNotes, handleBulkDelete],
  )
}
